import { DEFAULT_PREFERENCES, type Preferences, type UpdatePreferencesInput } from '@mystiq/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { keys, usePreferences, useWorkspaceId } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { ErrorLine } from '../components/ui.tsx';
import { choosingShortcut, shortcutFromEvent, shortcutLabel } from '../dictation/shortcut.ts';
import { ICON, Play } from '../icons.ts';

/** Languages offered as hints; any two-letter code works through the API. */
const LANGUAGES: Array<[code: string, name: string]> = [
  ['en', 'English'],
  ['hi', 'Hindi'],
  ['gu', 'Gujarati'],
  ['mr', 'Marathi'],
  ['bn', 'Bengali'],
  ['pa', 'Punjabi'],
  ['ta', 'Tamil'],
  ['te', 'Telugu'],
  ['kn', 'Kannada'],
  ['ml', 'Malayalam'],
  ['ur', 'Urdu'],
  ['es', 'Spanish'],
  ['fr', 'French'],
  ['de', 'German'],
  ['pt', 'Portuguese'],
  ['ar', 'Arabic'],
  ['zh', 'Chinese'],
  ['ja', 'Japanese'],
];
const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];

/** Personal settings: the same in every workspace and, later, in the desktop and iPhone apps. */
export function SettingsPage() {
  const ws = useWorkspaceId();
  const api = useApi();
  const client = useQueryClient();
  const preferences = usePreferences(ws);
  const voices = useQuery({ queryKey: keys.voices(ws), queryFn: () => api.voices(ws), staleTime: 10 * 60_000 });
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const preview = useRef<HTMLAudioElement | null>(null);

  // Shown at once, then saved; a refused change goes back to what is saved.
  const change = async (changes: UpdatePreferencesInput) => {
    setError(null);
    setSaved(false);
    client.setQueryData(keys.preferences(ws), (old: Preferences | undefined) => {
      const base = old ?? DEFAULT_PREFERENCES;
      return {
        languages: changes.languages ?? base.languages,
        dictation: { ...base.dictation, ...changes.dictation },
        readAloud: { ...base.readAloud, ...changes.readAloud },
      };
    });
    try {
      client.setQueryData(keys.preferences(ws), await api.updatePreferences(ws, changes));
      setSaved(true);
    } catch (caught) {
      setError(caught);
      void client.invalidateQueries({ queryKey: keys.preferences(ws) });
    }
  };

  // Choosing a shortcut: the next key combination pressed (Esc keeps the current one).
  useEffect(() => {
    if (!choosing) return;
    choosingShortcut.active = true;
    const onKey = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.key === 'Escape') {
        setChoosing(false);
        return;
      }
      const shortcut = shortcutFromEvent(event);
      if (!shortcut) return;
      setChoosing(false);
      void change({ dictation: { shortcut } });
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      choosingShortcut.active = false;
      window.removeEventListener('keydown', onKey, true);
    };
    // change is recreated each render; the listener only needs the latest when it fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choosing]);

  const current = preferences.data ?? DEFAULT_PREFERENCES;
  const speechService = (voices.data?.voices.length ?? 0) > 0;
  return (
    <div className="page-narrow">
      <h1 className="page-title">Settings</h1>
      <p className="meta" style={{ marginTop: 'var(--space-6)' }}>
        Yours alone, and the same in every workspace.
      </p>
      {(error || saved) && (
        <div className="content stack">
          <ErrorLine error={error} />
          {saved && (
            <p className="meta" role="status">
              Saved.
            </p>
          )}
        </div>
      )}

      <section className="section" aria-labelledby="languages-heading">
        <h2 className="section-heading" id="languages-heading">Languages you speak</h2>
        <fieldset className="field">
          <legend className="meta">Mystiq listens for these when you dictate and in your recordings. Choose up to five, or none to let it work out the language.</legend>
          <div className="choices">
            {LANGUAGES.map(([code, name]) => {
              const checked = current.languages.includes(code);
              return (
                <label className="choice" key={code}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!checked && current.languages.length >= 5}
                    onChange={() => void change({ languages: checked ? current.languages.filter((language) => language !== code) : [...current.languages, code] })}
                  />
                  {name}
                </label>
              );
            })}
          </div>
        </fieldset>
      </section>

      <section className="section" aria-labelledby="dictation-heading">
        <h2 className="section-heading" id="dictation-heading">Dictation</h2>
        <div className="stack">
          <p>
            Press <span className="state">{shortcutLabel(current.dictation.shortcut)}</span> while writing in a note or text field, speak, then press it again: your words are written where you were typing.
          </p>
          <p className="meta">It works while a Mystiq page is open and has the focus. It cannot type into other tabs or apps.</p>
          <div className="row-actions">
            <button className="button" onClick={() => setChoosing(true)} disabled={choosing}>
              {choosing ? 'Press the new shortcut now (Esc to cancel)' : 'Change shortcut'}
            </button>
            {current.dictation.shortcut !== DEFAULT_PREFERENCES.dictation.shortcut && (
              <button className="link-button" onClick={() => void change({ dictation: { shortcut: DEFAULT_PREFERENCES.dictation.shortcut } })}>
                Use {DEFAULT_PREFERENCES.dictation.shortcut} again
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="reading-heading">
        <h2 className="section-heading" id="reading-heading">Reading aloud</h2>
        <div className="stack">
          {speechService ? (
            <label className="field">
              <span className="field-label">Voice</span>
              <div className="inline-form">
                <select className="select" style={{ maxWidth: 480 }} value={current.readAloud.voice ?? ''} onChange={(event) => void change({ readAloud: { voice: event.target.value || null } })}>
                  <option value="">The default voice</option>
                  {voices.data!.voices.map((voice) => (
                    <option key={voice.id} value={voice.id}>
                      {voice.name}
                      {voice.languages.length ? ` (${voice.languages.join(', ')})` : ''}
                    </option>
                  ))}
                </select>
                {(() => {
                  const chosen = voices.data!.voices.find((voice) => voice.id === current.readAloud.voice);
                  return chosen?.previewUrl ? (
                    <button
                      className="button button-inline"
                      onClick={() => {
                        preview.current?.pause();
                        preview.current = new Audio(chosen.previewUrl!);
                        void preview.current.play().catch(() => undefined);
                      }}
                    >
                      <Play size={ICON.inline} stroke={ICON.stroke} />
                      Hear it
                    </button>
                  ) : null;
                })()}
              </div>
            </label>
          ) : (
            <p className="meta">{voices.isLoading ? 'Loading voices…' : "Your device's own voice reads aloud: no speech service is set up for Mystiq."}</p>
          )}
          <label className="field">
            <span className="field-label">Speed</span>
            <select className="select" style={{ maxWidth: 160 }} value={current.readAloud.speed} onChange={(event) => void change({ readAloud: { speed: Number(event.target.value) } })}>
              {SPEEDS.map((value) => (
                <option key={value} value={value}>
                  {value}×
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>
    </div>
  );
}
